<template>
  <section class="crit-panel">
    <header class="crit-panel__head">
      <h3 class="crit-panel__title">{{ titulo }}</h3>
      <div class="crit-panel__meta">
        <MatrizBadge :classificacao="classificacao || 'sem_classificacao'" size="md" />
      </div>
    </header>

    <div class="crit-panel__cols">
      <div
        v-for="eixo in eixos"
        :key="eixo.key"
        class="crit-col"
        :class="'crit-col--' + eixo.key"
      >
        <div class="crit-col__head">
          <span class="crit-col__label">{{ eixo.label }}</span>
          <span class="crit-col__total" :class="tone(eixo.score)">
            {{ eixo.score }}<small>/10</small>
          </span>
        </div>

        <ul class="crit-list">
          <li v-for="(c, i) in eixo.itens" :key="eixo.key + '-' + i" class="crit-item">
            <span class="crit-item__score" :class="tone(c.score)">{{ c.score }}</span>
            <div class="crit-item__text">
              <span class="crit-item__name">
                {{ criterioLabel(c.tipo) }}
                <HelpTip v-if="criterioHelpKey(c.tipo)" :tip="criterioHelpKey(c.tipo)" />
              </span>
              <span v-if="c.descricao" class="crit-item__desc">{{ c.descricao }}</span>
            </div>
          </li>
          <li v-if="!eixo.itens.length" class="crit-item crit-item--vazio">
            Sem critérios avaliados.
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script>
import MatrizBadge from "@/components/PortfolioAuditor/MatrizBadge.vue";
import HelpTip from "@/components/PortfolioAuditor/HelpTip.vue";
import { criterioHelpKey } from "@/utils/roboflexHelpTexts";

const LABEL_MAP = {
  resultados: "Entrega",
  orcamento: "Orçamento",
  cronograma: "Cronograma",
  entregas: "Pacotes de trabalho",
  progresso: "Progresso",
  custo: "Custo",
  prazos: "Prazo",
  atualizacao: "Atualização",
  coerencia: "Coerência",
};

export default {
  name: "CriteriosScorePanel",
  components: { MatrizBadge, HelpTip },
  props: {
    titulo: { type: String, required: true },
    classificacao: { type: String, default: "sem_classificacao" },
    scorePlanejamento: { type: Number, default: 0 },
    scoreExecucao: { type: Number, default: 0 },
    criterios: { type: Array, default: () => [] },
    excluirProgresso: { type: Boolean, default: false },
  },
  setup() {
    return { criterioHelpKey };
  },
  computed: {
    itens() {
      const arr = Array.isArray(this.criterios) ? this.criterios : [];
      return this.excluirProgresso ? arr.filter((c) => c.tipo !== "progresso") : arr;
    },
    eixos() {
      return [
        {
          key: "planejamento",
          label: "Planejamento",
          score: this.scorePlanejamento,
          itens: this.itens.filter((c) => c.eixo === "planejamento"),
        },
        {
          key: "execucao",
          label: "Execução",
          score: this.scoreExecucao,
          itens: this.itens.filter((c) => c.eixo === "execucao"),
        },
      ];
    },
  },
  methods: {
    criterioLabel(tipo) {
      return LABEL_MAP[tipo] || tipo;
    },
    tone(score) {
      if (score >= 7) return "ok";
      if (score >= 4) return "mid";
      return "bad";
    },
  },
};
</script>

<style scoped>
.crit-panel {
  border: 1px solid var(--b44-border, #e2e8f0);
  border-radius: 0.75rem;
  background: var(--b44-card, #fff);
  overflow: hidden;
}

.crit-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  background: var(--b44-head, #f8fafc);
  border-bottom: 1px solid var(--b44-border, #e2e8f0);
}

.crit-panel__title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--b44-fg, #0f172a);
}

.crit-panel__meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.crit-panel__cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.crit-col {
  min-width: 0;
  padding: 0.75rem 0.875rem 0.875rem;
}

.crit-col--planejamento {
  border-right: 1px solid var(--b44-border, #e2e8f0);
  background: var(--b44-grad-plan, linear-gradient(#f8fbff, #fff 4rem));
}

.crit-col--execucao {
  background: var(--b44-grad-exec, linear-gradient(#fffdf6, #fff 4rem));
}

.crit-col__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid var(--b44-border, #e2e8f0);
}

.crit-col__label {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.crit-col--planejamento .crit-col__label { color: #1d4ed8; }
.crit-col--execucao .crit-col__label { color: #b45309; }

.crit-col__total {
  font-size: 0.9375rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.crit-col__total small {
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--b44-muted, #94a3b8);
}

.crit-col__total.ok { color: #047857; }
.crit-col__total.mid { color: #b45309; }
.crit-col__total.bad { color: #b91c1c; }

.crit-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.crit-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px dashed var(--b44-border, #eef2f7);
}

.crit-item:last-child { border-bottom: 0; padding-bottom: 0; }
.crit-item:first-child { padding-top: 0; }

.crit-item__score {
  flex: none;
  min-width: 1.75rem;
  height: 1.75rem;
  padding: 0 0.375rem;
  border-radius: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  border: 1px solid transparent;
}

.crit-item__score.ok { background: #dcfce7; color: #15803d; border-color: #bbf7d0; }
.crit-item__score.mid { background: #fef3c7; color: #b45309; border-color: #fde68a; }
.crit-item__score.bad { background: #fee2e2; color: #b91c1c; border-color: #fecaca; }

.crit-item__text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.crit-item__name {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--b44-fg, #0f172a);
  line-height: 1.3;
}

.crit-item__desc {
  font-size: 0.75rem;
  line-height: 1.35;
  color: var(--b44-muted, #64748b);
  overflow-wrap: anywhere;
}

.crit-item--vazio {
  font-size: 0.75rem;
  color: var(--b44-muted, #94a3b8);
}

@media (max-width: 700px) {
  .crit-panel__cols { grid-template-columns: 1fr; }
  .crit-col--planejamento {
    border-right: 0;
    border-bottom: 1px solid var(--b44-border, #e2e8f0);
  }
}
</style>
